import { ChevronRight } from "@app/ui";
import type { ComponentType, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, ReactNode } from "react";
import { Reorder, useDragControls } from "../../lib/motion/index.js";
import { useI18n } from "../../lib/useI18n.js";
import { useSettingsStore } from "../../stores/index.js";
import type { FilterSectionKey } from "../../stores/settings.store.js";
import { useSidebarReorderProps } from "./useSidebarReorderProps.js";
import { CalendarList } from "../calendars/index.js";
import { LabelList } from "../labels/index.js";
import { PriorityFilter, UrgencyFilter } from "../priority/index.js";
import { TopicTree } from "../topics/index.js";

type SectionComp = ComponentType<{ grip?: ReactNode; onTitlePointerDown?: (e: ReactPointerEvent) => void }>;
const SECTION: Record<FilterSectionKey, SectionComp> = {
  topics: TopicTree,
  labels: LabelList,
  priority: PriorityFilter,
  urgency: UrgencyFilter,
  calendars: CalendarList,
};

/** The collapsible "Filters" group. Renders the filter sections in the
 *  user's saved order; drag a section's title text to reorder the sections
 *  (text-only so it doesn't collide with reordering the ENTRIES inside each
 *  list — see the per-list Reorder). Order persists + rides along in backups. */
export function FilterSidebar({ onContextMenu }: { onContextMenu?: (e: ReactMouseEvent) => void } = {}) {
  const { t } = useI18n();
  const collapsed = useSettingsStore((s) => s.sidebarCollapsed.filters);
  const setSidebarCollapsed = useSettingsStore((s) => s.setSidebarCollapsed);
  const order = useSettingsStore((s) => s.sidebarFilterOrder);
  const setOrder = useSettingsStore((s) => s.setSidebarFilterOrder);

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => void setSidebarCollapsed("filters", !collapsed)}
        onContextMenu={onContextMenu}
        aria-expanded={!collapsed}
        className="flex items-center gap-1 px-2 py-1 text-left"
      >
        <ChevronRight className={`h-3 w-3 shrink-0 text-muted-foreground transition-transform ${collapsed ? "" : "rotate-90"}`} />
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("common.filters")}</span>
      </button>
      {!collapsed ? (
        <Reorder.Group
          as="div"
          axis="y"
          values={order}
          onReorder={(next) => void setOrder(next as FilterSectionKey[])}
          className="flex flex-col gap-1 border-l border-border/60 pl-1"
        >
          {order.map((key) => <FilterItem key={key} value={key} />)}
        </Reorder.Group>
      ) : null}
    </div>
  );
}

function FilterItem({ value }: { value: FilterSectionKey }) {
  const controls = useDragControls();
  // Instant layout transitions during a sidebar resize/collapse gesture (and
  // under reduced motion) — see useSidebarReorderProps.
  const reorderProps = useSidebarReorderProps();
  const Section = SECTION[value];
  return (
    <Reorder.Item
      as="div"
      value={value}
      dragListener={false}
      dragControls={controls}
      {...reorderProps}
      className="rounded bg-card"
    >
      <Section onTitlePointerDown={(e) => controls.start(e)} />
    </Reorder.Item>
  );
}
